import Heading from 'LuxKit/Typography/Heading'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import * as React from 'react'
import { useLocation } from 'react-router-dom'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { FormattedMessage } from 'react-intl'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import MerchandiseSearchPageSort from './MerchandiseSearchPageSort'
import { setSearchParamValue, deleteSearchParams } from 'lib/url/searchUrlUtils'
import { useAppDispatch } from 'hooks/reduxHooks'
import { replaceWithRegion } from 'actions/NavigationActions'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import styled from 'styled-components'
import MerchandiseOfferCard from '../MerchandiseCards/MerchandiseOfferCard'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import type { MerchandiseFilterFacetForm } from './MerchandiseSearchFilters'
import MerchandiseSearchFilters from './MerchandiseSearchFilters'
import FilterChip from 'LuxKit/Chips/FilterChip'
import LineFilterAltIcon from 'LuxKit/Icons/line/LineFilterAltIcon'
import ModalContext from 'contexts/ModalContext'
import MerchandiseSearchFilterModal from './MerchandiseSearchFilterModal'
import { getMerchandiseQueryFromFilters, mapMerchandiseFilterFormToFilters, getMerchandiseFiltersFromQuery, getUrlCategoryIds, mergeFiltersWithPreservedData } from './merchandiseSearchPageUtils'
import { take } from 'lib/array/arrayUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import { useQsQueryParams } from 'hooks/useQueryParams'
import OfferListLoadMoreButton from 'components/OfferList/OfferListLoadMoreButton'
import debounce from 'lodash.debounce'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import type { OfferListEventHandler } from 'components/OfferList/OfferListEventsContext'
import { OfferListEventsProvider, OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { impressionEventWithContext, productClickEventWithContext } from 'analytics/snowplow/events'
import { trackEvent } from 'analytics/analytics'
import useHeldValue from 'hooks/useHeldValue'
import MerchandiseCardSkeleton from '../MerchandiseCards/MerchandiseCardSkeleton'
import { useInView } from 'react-intersection-observer'
import clsx from 'clsx'
import zIndex from 'styles/tools/z-index'
import MerchandiseSearchPageBreadcrumbs from './MerchandiseSearchPageBreadcrumbs'
import useMerchandiseLabels from 'hooks/Merchandise/useMerchandiseLabels'
import useMerchandiseSearchTracking from 'hooks/Merchandise/useMerchandiseSearchTracking'

const FullWidthContainer = styled.div`
  padding-left: ${rem(16)};
  padding-right: ${rem(16)};
  margin: 0 auto;
  width: 100%;

  ${mediaQueryUp.tablet} {
    padding-left: ${rem(60)};
    padding-right: ${rem(60)};
  }
`

const Content = styled.div`
 display: grid;
 grid-template-columns: 1fr;

 ${mediaQueryUp.desktop} {
  gap: ${rem(40)};
  grid-template-columns: ${rem(280)} 1fr;
 }
`

const Results = styled.div`
  display: grid;
  grid-auto-rows: min-content;
  grid-template-columns: 1fr 1fr;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    gap: ${rem(32)} ${rem(20)};
    grid-template-columns: repeat(auto-fill, minmax(${rem(200)}, 1fr));
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(auto-fill, minmax(${rem(210)}, 1fr));
  }

  ${mediaQueryUp.largeDesktop} {
    grid-template-columns: repeat(auto-fill, minmax(${rem(230)}, 1fr));
  }
`

const StickyFilterPanel = styled.fieldset`
  position: sticky;
  top: ${props => props.theme.layout.headerFloatingOffset};
  padding: ${rem(16)} 0;
  max-height: calc((var(--vh) * 100) - ${props => props.theme.layout.headerFloatingOffset});
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const StickyMobileHeaderContainer = styled.div`
  @media (max-width: 575.98px) {
    position: sticky;
    top: ${props => props.theme.layout.headerFloatingOffset};
    z-index: ${zIndex.stickyHeader};
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    padding: 0 ${rem(16)} ${rem(16)} ${rem(16)};
    margin-left: ${rem(-16)};
    margin-right: ${rem(-16)};
    margin-bottom: ${rem(32)};
    
    &.lifted {
      box-shadow: ${props => props.theme.shadow.bottom.small};
    }
    
    > *:not(:last-child) {
      margin-bottom: ${rem(16)};
    }
  }

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const ScrollAnchor = styled.div``

const EmptyStateContainer = styled.div`
  padding: ${rem(40)} 0;
  text-align: center;
`

const ClickableText = styled.span`
  color: #0066cc;
  cursor: pointer;
  text-decoration: underline;
  font-weight: 500;

  &:hover {
    opacity: 0.8;
  }
`

interface Props {
  category?: App.MerchandiseCategory
  brand?: App.MerchandiseBrand
  parentCategory?: App.MerchandiseCategory
  filters: App.MerchandiseFilters
  searchResults: Array<App.MerchandiseSearchAdvert>
  fetching: boolean
  urlPathSlug?: string
}

function MerchandiseSearchPageResults(props: Props) {
  const { filters, category, brand, searchResults, parentCategory, urlPathSlug } = props
  const location = useLocation()
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const { tagDisplayNames } = useMerchandiseLabels()

  const urlFilters = useMemo(() => getMerchandiseFiltersFromQuery(location.search), [location.search])
  const [anchorRef, anchorInView] = useInView({ initialInView: true })

  // Compute page title based on tags, brand, or category
  const pageTitle = useMemo(() => {
    // Priority: tag > brand > category
    if (filters.tags?.length) {
      const tagId = filters.tags[0]
      return tagDisplayNames[tagId] || tagId
    }
    if (brand) {
      return brand.name
    }
    if (parentCategory) {
      return parentCategory.name
    }
    return undefined
  }, [filters.tags, brand, parentCategory, tagDisplayNames])

  const { page = '1' } = useQsQueryParams<{ page: string }>()

  const prevSortBy = useRef(filters.sortBy)
  const sortByChanged = prevSortBy.current !== filters.sortBy

  useEffect(() => {
    prevSortBy.current = filters.sortBy
  })

  // Hold previous results during fetch, but only if cache has content
  const shouldHold = useCallback((current: Array<App.MerchandiseSearchAdvert>, held: Array<App.MerchandiseSearchAdvert>) => {
    if (sortByChanged) return false
    return props.fetching && held.length > 0
  }, [props.fetching, sortByChanged])

  const list = useHeldValue(searchResults, shouldHold)
  const showEmptyState = list.length === 0 && !props.fetching

  const showSkeleton = props.fetching && list.length === 0

  const onSortChange = useCallback((sortBy: App.MerchandiseSearchSort) => {
    const newSearch = setSearchParamValue(location.search, 'sortBy', sortBy ?? 'recommended')
    dispatch(replaceWithRegion(location.pathname, newSearch))
  }, [dispatch, location.pathname, location.search])

  const updateSearchFilters = useMemo(() => {
    return debounce((nextFilters: App.MerchandiseFilters) => {
      // Generate URL query params from filters
      const nextSearch = getMerchandiseQueryFromFilters(nextFilters)

      // Check if brand in URL path was deselected
      let newPath = location.pathname
      if (brand) {
        const match = location.pathname.match(/\/search\/shop\/brands\//)
        const isBrandPath = !!match
        const wasBrandSelected = filters.brands?.includes(brand.id)
        const isBrandSelected = nextFilters.brands?.includes(brand.id)

        // Remove brand from path if it was deselected
        if (isBrandPath && wasBrandSelected && !isBrandSelected) {
          newPath = '/search/shop'
        }
      }

      dispatch(replaceWithRegion(newPath, nextSearch))
    }, 300)
  },
  [dispatch, filters.brands, location.pathname, brand])

  const onLoadMore = useCallback(() => {
    const nextPage = parseInt(page) + 1
    const nextSearch = setSearchParamValue(location.search, 'page', nextPage.toString())
    dispatch(replaceWithRegion(location.pathname, nextSearch))
  }, [dispatch, location.pathname, location.search, page])

  const onSidebarFilterChange = useCallback((formFilters: MerchandiseFilterFacetForm) => {
    const newFilters = mapMerchandiseFilterFormToFilters(formFilters)
    const urlCategoryIds = getUrlCategoryIds(category, urlFilters)
    const mergedFilters = mergeFiltersWithPreservedData(newFilters, filters, urlCategoryIds)

    updateSearchFilters(mergedFilters)
  }, [updateSearchFilters, filters, category, urlFilters])

  const onResetFilters = useCallback(() => {
    // Delete all filter params from the URL
    const paramsToDelete = ['brands', 'campaigns', 'categories', 'tags', 'price.min', 'price.max', 'points.min', 'points.max', 'sortBy', 'search', 'page', 'freeShipping', 'earnRatio', 'onSale']

    // Also delete all dynamic options.* params
    const params = new URLSearchParams(location.search)
    params.forEach((_, key) => {
      if (key.startsWith('options.')) {
        paramsToDelete.push(key)
      }
    })

    const nextSearch = deleteSearchParams(location.search, ...paramsToDelete)
    // Remove filters slugs from the URL path since we're resetting all filters
    dispatch(replaceWithRegion('/search/shop', nextSearch))
  }, [dispatch, location.search])

  const onShowFilters = useCallback(async() => {
    // For progressive filtering in modal, strip out path categories from userFilters
    // Path categories will be passed separately via urlCategoryIds
    const modalFilters: App.MerchandiseFilters = {
      ...filters,
      // Only include query param categories, not path categories
      categories: urlFilters.categories,
    }

    const nextFilters = await showModal<App.MerchandiseFilters | null>(<MerchandiseSearchFilterModal
      userFilters={modalFilters}
      category={category}
      urlFilters={urlFilters}
    />)

    if (nextFilters === null) {
      // null indicates a reset action
      onResetFilters()
    } else if (nextFilters) {
      updateSearchFilters(nextFilters)
    }
  }, [showModal, filters, category, urlFilters, updateSearchFilters, onResetFilters])

  const itemCount = parseInt(page) * 12
  const hasMore = list.length > itemCount

  const tracking = useOfferListTracking(
    getListName('OfferList', 'Merchandise', 'Search Page Offer List'),
    {
      key: JSON.stringify(filters),
      snowplow: { enabled: false },
    },
  )

  const { searchContext, filterContexts, sortContext } = useMerchandiseSearchTracking({
    filters,
    fetching: props.fetching,
    urlPathSlug,
  })

  const onListEvent = useCallback<OfferListEventHandler>((dispatchAction) => {
    if (dispatchAction.type === OfferListEvents.productClick) {
      const { offer, position } = dispatchAction
      trackEvent(
        productClickEventWithContext(
          position + 1,
          tracking.listName,
          tracking.listId,
          offer,
          { searchContext, filterContexts, sortContext },
        ),
      )
    } else if (dispatchAction.type === OfferListEvents.productImpression) {
      const { offer, position } = dispatchAction
      trackEvent(
        impressionEventWithContext(
          position + 1,
          tracking.listName,
          tracking.listId,
          offer,
          { searchContext, filterContexts, sortContext },
        ),
      )
    }
  }, [tracking.listName, tracking.listId, searchContext, filterContexts, sortContext])

  return <FullWidthContainer>
    <Sheet>
      <ScrollAnchor ref={anchorRef} />
      <StickyMobileHeaderContainer className={clsx({ lifted: !anchorInView })}>
        <MerchandiseSearchPageBreadcrumbs />
        <Group
          direction="horizontal"
          gap={12}
          horizontalAlign="start"
          verticalAlign="center"
        >
          <FilterChip
            size="medium"
            endIcon={<LineFilterAltIcon />}
            onClick={onShowFilters}
          >
            <FormattedMessage
              id="bGgqfSFKSjTOticCxf38"
              defaultMessage="All filters"
              description="Button label to open the full filters panel in the search results interface"
            />
          </FilterChip>
          <MerchandiseSearchPageSort sortBy={filters.sortBy} onChange={onSortChange} />
        </Group>
      </StickyMobileHeaderContainer>
      <CSSBreakpoint min="tablet">
        <div style={{ marginBottom: '32px' }}>
          <MerchandiseSearchPageBreadcrumbs />
          <Group
            direction="horizontal"
            gap={12}
            horizontalAlign="start"
            desktopHorizontalAlign="space-between"
            verticalAlign="center"
          >
            <CSSBreakpoint min="desktop">
              <Group direction="vertical" gap={12}>
                {!!pageTitle && <Heading variant="heading2">
                  {pageTitle}
                </Heading>}
                <BodyText variant="large">
                  <FormattedMessage
                    id="foDIjMoXEQJyJsqjvW2k"
                    defaultMessage="Showing {count, plural, one {# result} other {# results}}"
                    description="Text showing number of search results in the search and discovery interface"
                    values={{ count: list.length }}
                  />
                </BodyText>
              </Group>
            </CSSBreakpoint>
            <MerchandiseSearchPageSort sortBy={filters.sortBy} onChange={onSortChange} />
          </Group>
        </div>
      </CSSBreakpoint>
      <VerticalSpacer gap={32}>
        <Content>
          <CSSBreakpoint min="desktop">
            <StickyFilterPanel>
              <MerchandiseSearchFilters
                userFilters={filters}
                category={category}
                urlFilters={urlFilters}
                onChange={onSidebarFilterChange}
                onReset={onResetFilters}
                showResetButton
              />
            </StickyFilterPanel>
          </CSSBreakpoint>
          <Sheet padding="0 0 64 0">
            {showSkeleton && <Results>
              {Array.from({ length: 18 }).map((_, index) => (
                <MerchandiseCardSkeleton key={index} />
              ))}
            </Results>}
            <OfferListEventsProvider tracking={tracking} onListEvent={onListEvent}>
              <Results>
                {take(list, itemCount).map((advert, index) => <MerchandiseOfferCard key={advert.id} id={advert.id} index={index} prioritisedVariantIds={advert.variantIds} />)}
              </Results>
            </OfferListEventsProvider>
            {showEmptyState && <EmptyStateContainer>
              <Group direction="vertical" gap={16} horizontalAlign="center">
                <BodyText variant="large">
                  <FormattedMessage
                    id="DfKzRjpmXsUQfdBXJ1-L"
                    defaultMessage="No products match your current filters."
                    description="Empty state message displayed when no merchandise products match the selected filters"
                  />
                </BodyText>
                <BodyText variant="medium">
                  <FormattedMessage
                    id="y0FQmLxY-iqMKlp3q0df"
                    defaultMessage="Try adjusting your filters or <resetLink>reset all filters</resetLink> to see more results."
                    description="Suggestion to adjust filters or reset them in the filters panel"
                    values={{
                      resetLink: (chunks: React.ReactNode) => (
                        <ClickableText onClick={onResetFilters}>{chunks}</ClickableText>
                      ),
                    }}
                  />
                </BodyText>
              </Group>
            </EmptyStateContainer>}
            {hasMore && <Group direction="horizontal" horizontalAlign="center">
              <OfferListLoadMoreButton
                onClick={onLoadMore}
                onScrollNear={onLoadMore}
                showLoadingIndicator={props.fetching}
              >
                <FormattedMessage
                  id="gb1beFFBsrqtlfhxScEC"
                  defaultMessage="Load more"
                  description="Action button label to load more results in the merchandise shop section"
                />
              </OfferListLoadMoreButton>
            </Group>}
          </Sheet>
        </Content>
      </VerticalSpacer>
    </Sheet>
  </FullWidthContainer>
}

export default MerchandiseSearchPageResults
